import type { Lang } from '@/i18n/config';

export const REFERENCE_SCENES = [
  'classroom',
  'motivation',
  'teacher-goal',
  'knowledge-map',
  'personal-route',
  'learning-cycle',
  'agency',
  'teacher-workspace',
  'technology',
  'finale',
] as const;

export type ReferenceSceneId = (typeof REFERENCE_SCENES)[number];
export type ExplanationDepth = 'teacher' | 'methodologist' | 'technical';

export interface ReferenceDemoState {
  sceneId: ReferenceSceneId;
  selectedStudent: number;
  causeStep: number;
  teacherGoal: number;
  graphMode: 'teacher' | 'student';
  graphLayers: {
    concepts: boolean;
    prerequisites: boolean;
    methods: boolean;
    errors: boolean;
    examples: boolean;
  };
  graphDrawerOpen: boolean;
  selectedGraphNode: string;
  profile: {
    knowledge: number;
    goal: number;
    pace: number;
    confidence: number;
    time: number;
    mistakes: number;
    motivation: number;
  };
  compareRoute: boolean;
  cycleStep: number;
  rewardMode: 'action' | 'mastery';
  teacherAction: number;
  explanationDepth: ExplanationDepth;
}

export type ReferenceDemoPatch = Partial<Omit<ReferenceDemoState, 'profile' | 'graphLayers'>> & {
  profile?: Partial<ReferenceDemoState['profile']>;
  graphLayers?: Partial<ReferenceDemoState['graphLayers']>;
};

export interface ReferenceSceneProps {
  lang: Lang;
  state: ReferenceDemoState;
  update: (patch: ReferenceDemoPatch) => void;
  openAssistant: (seed?: string) => void;
  navigate: (target: ReferenceSceneId | number) => void;
  reset: () => void;
  onInteraction?: (label: string) => void;
}

export type ReferencePresentationAction =
  | { type: 'navigate_to_scene'; scene: ReferenceSceneId }
  | { type: 'focus_student'; student: number }
  | { type: 'select_cause'; step: number }
  | { type: 'set_teacher_goal'; goal: number }
  | { type: 'focus_graph_node'; node: string }
  | { type: 'set_graph_layer'; layer: keyof ReferenceDemoState['graphLayers']; visible: boolean }
  | { type: 'set_profile'; field: keyof ReferenceDemoState['profile']; value: number }
  | { type: 'compare_routes'; enabled: boolean }
  | { type: 'set_cycle_step'; step: number }
  | { type: 'set_reward_mode'; mode: ReferenceDemoState['rewardMode'] }
  | { type: 'set_teacher_action'; action: number }
  | { type: 'set_explanation_depth'; depth: ExplanationDepth };

const SCENE_SET = new Set<string>(REFERENCE_SCENES);
const LAYERS = new Set<string>(['concepts', 'prerequisites', 'methods', 'errors', 'examples']);
const PROFILE_FIELDS = new Set<string>(['knowledge', 'goal', 'pace', 'confidence', 'time', 'mistakes', 'motivation']);
const DEPTHS = new Set<string>(['teacher', 'methodologist', 'technical']);

function integerIn(value: unknown, min: number, max: number): value is number {
  return Number.isInteger(value) && Number(value) >= min && Number(value) <= max;
}

export function validateReferenceActions(raw: unknown): ReferencePresentationAction[] {
  if (!Array.isArray(raw)) return [];
  const actions: ReferencePresentationAction[] = [];
  for (const candidate of raw.slice(0, 4)) {
    if (!candidate || typeof candidate !== 'object') continue;
    const action = candidate as Record<string, unknown>;
    switch (action.type) {
      case 'navigate_to_scene':
        if (typeof action.scene === 'string' && SCENE_SET.has(action.scene)) actions.push({ type: action.type, scene: action.scene as ReferenceSceneId });
        break;
      case 'focus_student':
        if (integerIn(action.student, 1, 12)) actions.push({ type: action.type, student: action.student });
        break;
      case 'select_cause':
        if (integerIn(action.step, 0, 6)) actions.push({ type: action.type, step: action.step });
        break;
      case 'set_teacher_goal':
        if (integerIn(action.goal, 0, 4)) actions.push({ type: action.type, goal: action.goal });
        break;
      case 'focus_graph_node':
        if (typeof action.node === 'string' && /^[a-z0-9-]{1,48}$/.test(action.node)) actions.push({ type: action.type, node: action.node });
        break;
      case 'set_graph_layer':
        if (typeof action.layer === 'string' && LAYERS.has(action.layer) && typeof action.visible === 'boolean') {
          actions.push({ type: action.type, layer: action.layer as keyof ReferenceDemoState['graphLayers'], visible: action.visible });
        }
        break;
      case 'set_profile':
        if (typeof action.field === 'string' && PROFILE_FIELDS.has(action.field) && integerIn(action.value, 0, 6)) {
          actions.push({ type: action.type, field: action.field as keyof ReferenceDemoState['profile'], value: action.value });
        }
        break;
      case 'compare_routes':
        if (typeof action.enabled === 'boolean') actions.push({ type: action.type, enabled: action.enabled });
        break;
      case 'set_cycle_step':
        if (integerIn(action.step, 0, 7)) actions.push({ type: action.type, step: action.step });
        break;
      case 'set_reward_mode':
        if (action.mode === 'action' || action.mode === 'mastery') actions.push({ type: action.type, mode: action.mode });
        break;
      case 'set_teacher_action':
        if (integerIn(action.action, 0, 3)) actions.push({ type: action.type, action: action.action });
        break;
      case 'set_explanation_depth':
        if (typeof action.depth === 'string' && DEPTHS.has(action.depth)) actions.push({ type: action.type, depth: action.depth as ExplanationDepth });
        break;
    }
  }
  return actions;
}

export const INITIAL_REFERENCE_STATE: ReferenceDemoState = {
  sceneId: 'classroom',
  selectedStudent: 8,
  causeStep: 3,
  teacherGoal: 0,
  graphMode: 'student',
  graphLayers: {
    concepts: true,
    prerequisites: true,
    methods: true,
    errors: true,
    examples: true,
  },
  graphDrawerOpen: true,
  selectedGraphNode: 'quadratic-equations',
  profile: {
    knowledge: 4,
    goal: 3,
    pace: 3,
    confidence: 3,
    time: 3,
    mistakes: 2,
    motivation: 4,
  },
  compareRoute: false,
  cycleStep: 2,
  rewardMode: 'mastery',
  teacherAction: 1,
  explanationDepth: 'teacher',
};

export const REFERENCE_SCENE_LABELS: Record<ReferenceSceneId, { ru: string; en: string }> = {
  classroom: { ru: 'Класс', en: 'Class' },
  motivation: { ru: 'Причина', en: 'Cause' },
  'teacher-goal': { ru: 'Направление', en: 'Direction' },
  'knowledge-map': { ru: 'Карта знаний', en: 'Knowledge map' },
  'personal-route': { ru: 'Маршрут', en: 'Route' },
  'learning-cycle': { ru: 'Цикл', en: 'Cycle' },
  agency: { ru: 'Я могу', en: 'I can' },
  'teacher-workspace': { ru: 'Решение учителя', en: 'Teacher action' },
  technology: { ru: 'Технология', en: 'Technology' },
  finale: { ru: 'Связь', en: 'Connection' },
};

interface ReferenceTranscript {
  title: string;
  summary: string;
}

export const REFERENCE_TRANSCRIPTS: Record<ReferenceSceneId, Record<Lang, ReferenceTranscript>> = {
  classroom: {
    ru: {
      title: 'Увидеть причину. Выбрать действие.',
      summary: 'Экран учителя показывает двенадцать синтетических учеников как систему связанных сигналов. Выбранный сигнал раскрывает вероятную причину учебного затруднения и безопасное педагогическое действие.',
    },
    en: {
      title: 'See the cause. Choose an action.',
      summary: 'The teacher sees twelve synthetic learners as a connected signal system. A selected signal reveals a probable learning cause and a bounded teaching action.',
    },
  },
  motivation: {
    ru: {
      title: 'Почему пропадает мотивация',
      summary: 'Интерактивная причинная цепочка связывает пропущенную концепцию, пробелы, повторяющиеся ошибки, усилия без результата, потерю уверенности и контроля с падением мотивации.',
    },
    en: {
      title: 'Why motivation disappears',
      summary: 'An interactive causal chain connects a missed concept, prerequisite gaps, repeated errors, effort without visible progress, loss of confidence and control, and declining motivation.',
    },
  },
  'teacher-goal': {
    ru: {
      title: 'Учитель задаёт направление',
      summary: 'Учитель выбирает педагогическую цель. Система перестраивает горизонт обучения, глубину диагностики, формат заданий и паттерн рекомендаций, сохраняя решение за педагогом.',
    },
    en: {
      title: 'The teacher sets the direction',
      summary: 'The teacher selects the pedagogical goal. The system reshapes the learning horizon, diagnostic depth, task format and recommendation pattern while leaving the decision with the educator.',
    },
  },
  'knowledge-map': {
    ru: {
      title: 'Карта знаний: алгебра',
      summary: 'Граф раскрывает концепции, предпосылки, методы решения, типичные ошибки и примеры вокруг квадратных уравнений. Цвет показывает уровень освоения и помогает подобрать следующий набор заданий.',
    },
    en: {
      title: 'Knowledge map: algebra',
      summary: 'The graph exposes concepts, prerequisites, solution methods, typical errors and examples around quadratic equations. Color communicates mastery and supports the next task selection.',
    },
  },
  'personal-route': {
    ru: {
      title: 'Маршрут меняется вместе с учеником',
      summary: 'Путь пересчитывается после изменения уровня знаний, цели, темпа, уверенности, доступного времени, типичных ошибок и мотивации. Экран объясняет, почему выбран следующий шаг.',
    },
    en: {
      title: 'The route changes with the learner',
      summary: 'The path is recalculated when knowledge, goal, pace, confidence, available time, typical errors or motivation change. The screen explains why the next step was selected.',
    },
  },
  'learning-cycle': {
    ru: {
      title: 'Обучение — это цикл, а не один урок',
      summary: 'Восемь этапов связывают цель учителя, текущее состояние ученика, доступный следующий шаг, короткий учебный блок, анализ ответа, обновление карты, сигнал учителю и коррекцию маршрута.',
    },
    en: {
      title: 'Learning is a cycle, not one lesson',
      summary: 'Eight stages connect the teacher goal, learner state, available next step, short learning block, response analysis, map update, teacher signal and route correction.',
    },
  },
  agency: {
    ru: {
      title: 'Мотивация начинается с ощущения: я могу',
      summary: 'Экран показывает ближайшую понятную цель, видимое доказательство прогресса, достижимый следующий шаг, выбор маршрута и своевременную поддержку тьютора.',
    },
    en: {
      title: 'Motivation begins with: I can',
      summary: 'The screen combines a clear near goal, visible evidence of progress, an achievable next step, route choice and timely tutor support.',
    },
  },
  'teacher-workspace': {
    ru: {
      title: 'Рабочее пространство учителя',
      summary: 'Сигналы класса раскрываются через доказательства, вероятный пробел и набор педагогических вмешательств. После выбора действия система показывает обновлённый маршрут ученика.',
    },
    en: {
      title: 'Teacher workspace',
      summary: 'Class signals are connected to an evidence trail, a probable gap and a set of teaching interventions. Once an action is selected, the system displays the updated learner route.',
    },
  },
  technology: {
    ru: {
      title: 'Что работает сегодня. Что исследуем дальше.',
      summary: 'Экран честно разделяет продуктовые механизмы — граф знаний, адаптивную диагностику, микроуроки, мастерство, личный маршрут, рекомендации и AI‑тьютора — и отдельные научные прототипы.',
    },
    en: {
      title: 'What works today. What we explore next.',
      summary: 'The screen separates product mechanisms—knowledge graph, adaptive diagnosis, micro-lessons, mastery, personal routes, recommendations and the AI tutor—from distinct research prototypes.',
    },
  },
  finale: {
    ru: {
      title: 'Учитель решает. Ученик движется. Система связывает.',
      summary: 'Финальный экран объединяет педагогический сигнал, путь ученика и адаптивную систему в непрерывный цикл. Пользователь может спросить о сцене, ученике или маршруте.',
    },
    en: {
      title: 'Teacher decides. Learner moves. System connects.',
      summary: 'The final screen connects the teaching signal, learner path and adaptive system into a continuous loop. The visitor can ask about a scene, learner or route.',
    },
  },
};
